<template>
  <div class="changeOrderOverview">
    <tabs :preselected-index="0" :tab-names="tabNames" @select-tab="selectTab">
      <tab :title="tabNames[0]" :index="0">
        <div class="container">
          <change-order-overview-filter @update="filtersUpdated" />
          <div class="overview-block">
            <div class="header singleRow">
              <div class="title">
                {{ i18n.t('/change-order/dashboard/order') }}
              </div>
              <div class="salesemployee">
                {{ i18n.t('/change-order/dashboard/name') }}
              </div>
              <div class="status">{{ i18n.t('/Dashboard/Headers/Status') }}</div>
              <div class="actions" />
            </div>
            <ul>
              <change-order-row
                v-for="changeOrder in changeOrders"
                :key="changeOrder.ChangeOrderId"
                :change-order="changeOrder"
              />
            </ul>
          </div>
        </div>
      </tab>
      <tab v-if="isBulkOrderButtonVisible" :title="tabNames[1]" :index="1">
        <div class="container">
          <change-order-overview-filter @update="filtersUpdated" />
          <div v-if="isBulkOrderButtonVisible" class="overview-block">
            <div class="header bulkRow">
              <div class="bulkChangeOrderId">
                {{ i18n.t('/change-order/dashboard/id') }}
              </div>
              <div class="date">
                {{ i18n.t('/change-order/dashboard/date') }}
              </div>
              <div class="komNumbers">
                {{ i18n.t('/change-order/dashboard/kom-numbers') }}
              </div>
              <div class="salesemployee">
                {{ i18n.t('/change-order/dashboard/name') }}
              </div>
              <div class="status">{{ i18n.t('/Dashboard/Headers/Status') }}</div>
              <div class="actions">&nbsp;</div>
            </div>
            <ul>
              <bulk-change-order-row
                v-for="bulkChangeOrder in bulkChangeOrders"
                :key="bulkChangeOrder.BulkChangeOrderId"
                :bulk-change-order="bulkChangeOrder"
                @pause="updateBulkChangeRowStatus(bulkChangeOrder, BulkChangeOrderStatus.paused)"
                @resume="
                  updateBulkChangeRowStatus(
                    bulkChangeOrder,
                    BulkChangeOrderStatus.readyForProcessing
                  )
                "
              />
            </ul>
          </div>
        </div>
      </tab>
    </tabs>
    <div class="container">
      <div class="loaderWrapper">
        <loader v-if="isLoading" />
      </div>
      <div>
        <app-button data-qa="load-change-orders-button" @click="loadMore">{{
          i18n.t('/Dashboard/LoadMore')
        }}</app-button>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import StoreConnector from '@/helpers/StoreConnector'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import ChangeOrderRow from '@/components/changeOrder/presentational/organisms/changeOrderOverview/changeOrderRow.vue'
import BulkChangeOrderRow from '@/components/changeOrder/presentational/organisms/changeOrderOverview/BulkChangeOrderRow.vue'
import changeOrderService from '@/services/changeOrderService'
import bulkChangeOrderService from '@/services/bulkChangeOrderService'
import { mapGetters } from 'vuex'
import Tab from '@/components/generic/presentational/molecules/tabs/Tab.vue'
import Tabs from '@/components/generic/presentational/molecules/tabs/Tabs.vue'
import ChangeOrderOverviewFilter from '@/components/changeOrder/presentational/organisms/changeOrderOverview/ChangeOrderOverviewFilter.vue'
import BulkChangeOrderStatus from '@/helpers/enums/bulkChangeOrderStatus'

export default {
  components: {
    AppButton,
    Loader,
    ChangeOrderRow,
    BulkChangeOrderRow,
    Tab,
    Tabs,
    ChangeOrderOverviewFilter,
  },
  data() {
    return {
      changeOrders: [],
      bulkChangeOrders: [],
      currentPage: 1,
      isLoading: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      activeTab: 'changeOrders',
      BulkChangeOrderStatus: BulkChangeOrderStatus,
    }
  },
  computed: {
    ...mapGetters({
      dealerSettings: 'user/dealerSettings',
      showOnlyOwnChangeOrders: 'changeOrders/showOnlyOwnChangeOrders',
      searchQuery: 'changeOrders/searchQuery',
    }),
    isBulkOrderButtonVisible() {
      return this.dealerSettings.isImporter
    },
    tabNames() {
      let tabs = ["Wijzigingen bestelde auto's"]
      if (this.isBulkOrderButtonVisible) {
        tabs.push('Bulk wijziging')
      }
      return tabs
    },
  },
  async created() {
    StoreConnector.get('changeOrder/all', (data) => {
      this.changeOrders = data
    })
    StoreConnector.get('bulkChangeOrder/allBulkChangeOrders', (data) => {
      this.bulkChangeOrders = data
    })

    await this.fetchChangeOrders()
    await this.fetchBulkChangeOrders()

    this.refreshInterval = setInterval(() => {
      this.silentFetchBulkChangeOrders()
    }, 30000)
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    selectTab(newTabIndex) {
      if (newTabIndex === 0) this.activeTab = 'changeOrders'
      if (newTabIndex === 1) this.activeTab = 'bulkChangeOrders'
    },
    async filtersUpdated() {
      this.currentPage = 1

      if (this.activeTab === 'changeOrders') {
        changeOrderService.resetChangeOrders()
        await this.fetchChangeOrders()
      }
      if (this.activeTab === 'bulkChangeOrders') {
        bulkChangeOrderService.resetBulkChangeOrders()
        await this.fetchBulkChangeOrders()
      }
    },
    async loadMore() {
      this.currentPage++
      if (this.activeTab === 'changeOrders') {
        await this.fetchChangeOrders()
      }
      if (this.activeTab === 'bulkChangeOrders') {
        await this.fetchBulkChangeOrders()
      }
    },
    async fetchChangeOrders() {
      this.isLoading = true
      const includeNationWide = !this.showOnlyOwnChangeOrders
      const query = this.searchQuery.length > 0 ? this.searchQuery : null

      if (this.currentPage === 1) changeOrderService.resetChangeOrders()

      await changeOrderService.getChangeOrders(this.currentPage, includeNationWide, query)

      this.isLoading = false
    },
    async fetchBulkChangeOrders() {
      if (!this.isBulkOrderButtonVisible) return

      this.isLoading = true
      const includeNationWide = !this.showOnlyOwnChangeOrders
      const query = this.searchQuery.length > 0 ? this.searchQuery : null

      if (this.currentPage === 1) bulkChangeOrderService.resetBulkChangeOrders()

      try {
        await bulkChangeOrderService.getBulkChangeOrders(this.currentPage, includeNationWide, query)
      } catch {}

      this.isLoading = false
    },
    async silentFetchBulkChangeOrders() {
      if (this.activeTab === 'bulkChangeOrders') {
        const includeNationWide = !this.showOnlyOwnChangeOrders
        const query = this.searchQuery.length > 0 ? this.searchQuery : null

        try {
          await bulkChangeOrderService.getBulkChangeOrders(
            this.currentPage,
            includeNationWide,
            query
          )
        } catch {}
      }
    },
    async submit() {
      this.currentPage = 1
      if (this.activeTab === 'changeOrders') {
        await this.fetchChangeOrders()
      }
      if (this.activeTab === 'bulkChangeOrders') {
        await this.fetchBulkChangeOrders()
      }
    },
    updateBulkChangeRowStatus(bulkChangeOrder, status) {
      bulkChangeOrder.BulkOrderStatus = status
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  @include center;
  display: grid;
  row-gap: 20px;
}

:deep() {
  .singleRow {
    grid-template-columns: 13fr repeat(2, 10fr) 10fr;
    grid-template-areas: 'title salesemployee status actions';

    border-bottom: 1px solid #dae2e5;
    padding: 0.5rem;
    grid-column-gap: 0.5rem;
    display: grid;
  }

  .bulkRow {
    grid-template-columns: 5em 10em 10em 15em 10em 10em;
    grid-template-areas: 'bulkChangeOrderId date komNumbers salesemployee status actions';

    border-bottom: 1px solid #dae2e5;
    padding: 0.5rem;
    grid-column-gap: 0.5rem;
    display: grid;
  }

  li.singleRow:hover {
    background-color: #ebe9e91f;
  }
  li.bulkRow:hover {
    background-color: #ebe9e91f;
  }
}

.changeOrderOverview {
  display: grid;
  row-gap: 20px;
}

.header {
  background-color: #f1f4f5;
  font-weight: $font-weight-bold;
}

ul {
  list-style: none outside;
}

.loaderWrapper {
  display: grid;
}

.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tabs button {
  padding: 10px 20px;
  border: none;
  background-color: #f1f4f5;
  cursor: pointer;
}

.tabs button.active {
  background-color: #dae2e5;
  font-weight: bold;
}
</style>
